import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  getDoc,
  doc,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { CMSPage, CMSPageCategory, LanguageCode } from '../types';

// Default language for fallback
const DEFAULT_LANGUAGE: LanguageCode = 'en';

/**
 * Fetch all published CMS pages for a specific language
 * Falls back to default language if translation is not available
 */
export async function fetchPublishedPages(
  language: LanguageCode = DEFAULT_LANGUAGE
): Promise<CMSPage[]> {
  const pagesRef = collection(db, 'cms_pages');

  // First, try to get pages in the requested language
  const q = query(
    pagesRef,
    where('status', '==', 'published'),
    where('language', '==', language),
    orderBy('order', 'asc')
  );

  const snapshot = await getDocs(q);
  const pages = snapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    ...docSnap.data(),
  })) as CMSPage[];

  // If not the default language, also fetch default language pages
  // to fill in missing translations
  if (language !== DEFAULT_LANGUAGE) {
    const defaultQ = query(
      pagesRef,
      where('status', '==', 'published'),
      where('language', '==', DEFAULT_LANGUAGE),
      orderBy('order', 'asc')
    );

    const defaultSnapshot = await getDocs(defaultQ);
    const defaultPages = defaultSnapshot.docs.map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data(),
    })) as CMSPage[];

    // Add default language pages that don't have a translation
    const translatedBaseSlugs = new Set(pages.map((p) => p.baseSlug));
    const missingTranslations = defaultPages.filter(
      (p) => !translatedBaseSlugs.has(p.baseSlug)
    );

    return [...pages, ...missingTranslations];
  }

  return pages;
}

/**
 * Fetch published pages by category for a specific language
 * Falls back to default language if translation is not available
 */
export async function fetchPagesByCategory(
  category: CMSPageCategory,
  language: LanguageCode = DEFAULT_LANGUAGE
): Promise<CMSPage[]> {
  const pagesRef = collection(db, 'cms_pages');

  // First, try to get pages in the requested language
  const q = query(
    pagesRef,
    where('status', '==', 'published'),
    where('category', '==', category),
    where('language', '==', language),
    orderBy('order', 'asc')
  );

  const snapshot = await getDocs(q);
  const pages = snapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    ...docSnap.data(),
  })) as CMSPage[];

  // If not the default language, also fetch default language pages
  // to fill in missing translations
  if (language !== DEFAULT_LANGUAGE) {
    const defaultQ = query(
      pagesRef,
      where('status', '==', 'published'),
      where('category', '==', category),
      where('language', '==', DEFAULT_LANGUAGE),
      orderBy('order', 'asc')
    );

    const defaultSnapshot = await getDocs(defaultQ);
    const defaultPages = defaultSnapshot.docs.map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data(),
    })) as CMSPage[];

    // Add default language pages that don't have a translation
    const translatedBaseSlugs = new Set(pages.map((p) => p.baseSlug));
    const missingTranslations = defaultPages.filter(
      (p) => !translatedBaseSlugs.has(p.baseSlug)
    );

    return [...pages, ...missingTranslations];
  }

  return pages;
}

/**
 * Fetch a single page by baseSlug for a specific language
 * Falls back to default language if translation is not available
 *
 * @param baseSlug - The base slug shared across all translations (e.g., "about-us")
 * @param language - The desired language code (e.g., "en", "es")
 */
export async function fetchPageBySlug(
  baseSlug: string,
  language: LanguageCode = DEFAULT_LANGUAGE
): Promise<CMSPage | null> {
  const pagesRef = collection(db, 'cms_pages');

  // First, try to get the page in the requested language
  const q = query(
    pagesRef,
    where('baseSlug', '==', baseSlug),
    where('language', '==', language),
    where('status', '==', 'published')
  );

  const snapshot = await getDocs(q);

  if (!snapshot.empty) {
    const docSnap = snapshot.docs[0];
    return {
      id: docSnap.id,
      ...docSnap.data(),
    } as CMSPage;
  }

  // Fallback to default language if translation not found
  if (language !== DEFAULT_LANGUAGE) {
    const fallbackQ = query(
      pagesRef,
      where('baseSlug', '==', baseSlug),
      where('language', '==', DEFAULT_LANGUAGE),
      where('status', '==', 'published')
    );

    const fallbackSnapshot = await getDocs(fallbackQ);

    if (!fallbackSnapshot.empty) {
      const docSnap = fallbackSnapshot.docs[0];
      return {
        id: docSnap.id,
        ...docSnap.data(),
      } as CMSPage;
    }
  }

  return null;
}

/**
 * Fetch a single page by its localized slug (for backwards compatibility)
 * This searches by the actual slug field, not baseSlug
 *
 * @param slug - The localized slug (e.g., "about-us" or "sobre-nosotros")
 * @param language - Optional language to filter by
 */
export async function fetchPageByLocalizedSlug(
  slug: string,
  language?: LanguageCode
): Promise<CMSPage | null> {
  const pagesRef = collection(db, 'cms_pages');

  // Build query based on whether language is specified
  const constraints = [
    where('slug', '==', slug),
    where('status', '==', 'published'),
  ];

  if (language) {
    constraints.push(where('language', '==', language));
  }

  const q = query(pagesRef, ...constraints);
  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    return null;
  }

  const docSnap = snapshot.docs[0];
  return {
    id: docSnap.id,
    ...docSnap.data(),
  } as CMSPage;
}

/**
 * Fetch a single page by custom URL path
 * Used for SEO-friendly URLs like /about-us, /es/sobre-nosotros
 * The URL path already includes the language prefix, so no fallback is needed
 */
export async function fetchPageByUrlPath(urlPath: string): Promise<CMSPage | null> {
  const pagesRef = collection(db, 'cms_pages');
  const q = query(
    pagesRef,
    where('urlPath', '==', urlPath),
    where('status', '==', 'published')
  );

  const snapshot = await getDocs(q);
  if (snapshot.empty) {
    return null;
  }

  const docSnap = snapshot.docs[0];
  return {
    id: docSnap.id,
    ...docSnap.data(),
  } as CMSPage;
}

/**
 * Fetch a page by URL path with language fallback
 * Tries the localized path first, then falls back to default language path
 *
 * @param basePath - The base URL path without language prefix (e.g., "/about-us")
 * @param language - The desired language code
 */
export async function fetchPageByUrlPathWithFallback(
  basePath: string,
  language: LanguageCode = DEFAULT_LANGUAGE
): Promise<CMSPage | null> {
  const pagesRef = collection(db, 'cms_pages');

  // Construct the localized path
  const localizedPath = language === DEFAULT_LANGUAGE
    ? basePath
    : `/${language}${basePath}`;

  // First, try the localized path
  const q = query(
    pagesRef,
    where('urlPath', '==', localizedPath),
    where('status', '==', 'published')
  );

  const snapshot = await getDocs(q);

  if (!snapshot.empty) {
    const docSnap = snapshot.docs[0];
    return {
      id: docSnap.id,
      ...docSnap.data(),
    } as CMSPage;
  }

  // Fallback to default language path if not found
  if (language !== DEFAULT_LANGUAGE) {
    const fallbackQ = query(
      pagesRef,
      where('urlPath', '==', basePath),
      where('status', '==', 'published')
    );

    const fallbackSnapshot = await getDocs(fallbackQ);

    if (!fallbackSnapshot.empty) {
      const docSnap = fallbackSnapshot.docs[0];
      return {
        id: docSnap.id,
        ...docSnap.data(),
      } as CMSPage;
    }
  }

  return null;
}

/**
 * Fetch a single page by ID
 * Note: This doesn't need language fallback as IDs are unique
 */
export async function fetchPageById(id: string): Promise<CMSPage | null> {
  const docRef = doc(db, 'cms_pages', id);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  const data = docSnap.data();
  // Only return if published (for public access)
  if (data.status !== 'published') {
    return null;
  }

  return {
    id: docSnap.id,
    ...data,
  } as CMSPage;
}

/**
 * Fetch pages for a specific zone in a specific language
 * Falls back to default language if translation is not available
 */
export async function fetchPagesByZone(
  zoneId: string,
  language: LanguageCode = DEFAULT_LANGUAGE
): Promise<CMSPage[]> {
  const pagesRef = collection(db, 'cms_pages');

  // First, try to get pages in the requested language
  const q = query(
    pagesRef,
    where('status', '==', 'published'),
    where('zoneId', '==', zoneId),
    where('language', '==', language),
    orderBy('order', 'asc')
  );

  const snapshot = await getDocs(q);
  const pages = snapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    ...docSnap.data(),
  })) as CMSPage[];

  // If not the default language, also fetch default language pages
  // to fill in missing translations
  if (language !== DEFAULT_LANGUAGE) {
    const defaultQ = query(
      pagesRef,
      where('status', '==', 'published'),
      where('zoneId', '==', zoneId),
      where('language', '==', DEFAULT_LANGUAGE),
      orderBy('order', 'asc')
    );

    const defaultSnapshot = await getDocs(defaultQ);
    const defaultPages = defaultSnapshot.docs.map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data(),
    })) as CMSPage[];

    // Add default language pages that don't have a translation
    const translatedBaseSlugs = new Set(pages.map((p) => p.baseSlug));
    const missingTranslations = defaultPages.filter(
      (p) => !translatedBaseSlugs.has(p.baseSlug)
    );

    return [...pages, ...missingTranslations];
  }

  return pages;
}

/**
 * Get all available translations for a page by its baseSlug
 */
export async function fetchPageTranslations(
  baseSlug: string
): Promise<CMSPage[]> {
  const pagesRef = collection(db, 'cms_pages');
  const q = query(
    pagesRef,
    where('baseSlug', '==', baseSlug),
    where('status', '==', 'published')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    ...docSnap.data(),
  })) as CMSPage[];
}

/**
 * Check if a translation exists for a specific language
 */
export async function hasTranslation(
  baseSlug: string,
  language: LanguageCode
): Promise<boolean> {
  const pagesRef = collection(db, 'cms_pages');
  const q = query(
    pagesRef,
    where('baseSlug', '==', baseSlug),
    where('language', '==', language),
    where('status', '==', 'published')
  );

  const snapshot = await getDocs(q);
  return !snapshot.empty;
}
